import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, Zap, DollarSign, CheckCircle2, Sparkles } from "lucide-react";
import { workforceApi } from "../../lib/workforce";
import BrainBadge from "../../components/workforce/BrainBadge";

const STATUS = {
  available: { label: "Available", color: "#10B981" },
  working:   { label: "Working",   color: "#F59E0B" },
  waiting:   { label: "Waiting",   color: "#3B82F6" },
  error:     { label: "Error",     color: "#EF4444" },
};

export default function MyWorkforce() {
  const [workers, setWorkers] = useState([]);
  const [brainMap, setBrainMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([workforceApi.listWorkers(), workforceApi.listBrains()])
      .then(([w, b]) => {
        setWorkers(w.workers || []);
        const map = {};
        (b.brains || []).forEach((x) => { map[x.id] = x; });
        setBrainMap(map);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalCost = workers.reduce((s, w) => s + (w.cost_total || 0), 0);
  const totalTasks = workers.reduce((s, w) => s + (w.tasks_completed || 0), 0);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="my-workforce-page">
      {/* Header */}
      <div className="brut-card p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-200 opacity-40 float-y" />
        <div className="absolute right-32 -bottom-8 w-24 h-24 rounded-full bg-emerald-200 opacity-40 float-y" style={{ animationDelay: "1.5s" }} />
        <div className="relative flex flex-wrap items-start gap-6 justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Agent Workforce</div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-2">My AI Workforce</h1>
            <p className="text-slate-600 max-w-xl">
              Build specialized AI workers and power them with the model of your choice.
              <br />
              <span className="font-bold text-slate-900">Build the worker. Choose the brain.</span>
            </p>
          </div>
          <Link to="/workforce/new" className="brut-btn" data-testid="create-worker-btn">
            <Plus size={16} strokeWidth={2.5} /> Hire a worker
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="workforce-stats">
        <Stat icon={Users} label="Workers"    value={workers.length}         color="#3B82F6" />
        <Stat icon={CheckCircle2} label="Tasks Done" value={totalTasks}    color="#10B981" />
        <Stat icon={DollarSign} label="Total Cost" value={`$${totalCost.toFixed(4)}`} color="#F59E0B" />
        <Stat icon={Zap}      label="Live Brains" value={Object.keys(brainMap).length} color="#8B5CF6" />
      </div>

      {/* Workers grid */}
      {loading ? (
        <div className="text-center py-16 text-slate-400 italic">Loading your workforce…</div>
      ) : workers.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="worker-grid">
          {workers.map((w) => (
            <WorkerCard key={w.worker_id} worker={w} brain={brainMap[(w.brain || {}).brain_id]} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: I, label, value, color }) {
  return (
    <div className="brut-card p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl border-2 flex items-center justify-center"
           style={{ backgroundColor: color + "1F", borderColor: color, color }}>
        <I size={18} strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="font-heading font-bold text-xl leading-none">{value}</div>
      </div>
    </div>
  );
}

function WorkerCard({ worker, brain }) {
  const status = STATUS[worker.status] || STATUS.available;
  return (
    <Link
      to={`/workforce/worker/${worker.worker_id}`}
      data-testid={`worker-card-${worker.worker_id}`}
      className="brut-card p-5 flex flex-col gap-3 group"
    >
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-3xl shrink-0">
          {worker.avatar || "👤"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-lg leading-tight truncate">{worker.name}</div>
          <div className="text-sm text-slate-600 truncate">{worker.title}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">{worker.department}</div>
        </div>
        <span
          className="text-[10px] uppercase font-bold flex items-center gap-1 shrink-0"
          style={{ color: status.color }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.color }} />
          {status.label}
        </span>
      </div>

      {worker.description && (
        <p className="text-xs text-slate-600 line-clamp-2">{worker.description}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {brain ? <BrainBadge brain={brain} size="sm" showFamily /> : <span className="text-xs text-slate-400 italic">No brain</span>}
        {worker.auto_brain && (
          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-100 border border-amber-500 text-amber-700">
            Auto Brain
          </span>
        )}
      </div>

      <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-200 text-slate-500">
        <span>{worker.tasks_completed || 0} tasks</span>
        <span>{(worker.tools || []).length} tools</span>
        <span className="font-mono font-bold text-slate-700">${(worker.cost_total || 0).toFixed(4)}</span>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="brut-card p-12 text-center max-w-2xl mx-auto" data-testid="workforce-empty">
      <div className="text-6xl mb-4 float-y inline-block">🧑‍💼</div>
      <h2 className="font-heading text-2xl font-bold mb-2">No workers yet</h2>
      <p className="text-slate-600 mb-6">
        Start by hiring your first AI worker. Give them a name, a job, a persona, and choose the brain that powers them. You can switch the brain anytime without losing anything.
      </p>
      <Link to="/workforce/new" className="brut-btn" data-testid="create-first-btn">
        <Sparkles size={16} strokeWidth={2.5} /> Hire your first worker
      </Link>
    </div>
  );
}
